const getPrefixedKey = (s: string): string => `higharc:${s}`;

/**
 * Sentry
 */

export const SERVER_SENTRY_URL = "https://d4a4848ade1b45d5a638ec7ed0812c31@o508894.ingest.sentry.io/5623279";
export const WORKER_SENTRY_URL = "https://07d225c973654f978cf2d8a744e36e2d@o508894.ingest.sentry.io/5623288";
export const UI_SENTRY_URL = "https://8c6f6a2042a54a5cb5b3a78facfdfb7d@o508894.ingest.sentry.io/5623265";

export const DEBUG_PAGE_REGEX = /\/debug\//i;

/**
 * Links to documentation outside of the application code.
 */

export const HIGHARC_DOCS_NAME = "Higharc Knowledge Center";

export const DOC_URLS = {
  CUSTOMER_NEEDS: "https://linear.app/higharc/customers",
  FEATURE_FLAGS: "https://www.notion.so/higharc/Feature-Flags-c4a70e5576ec41659a87e7d760223f4b",
  HIGHARC_DOCS: {
    BASE: "https://help.higharc.com",
    INTEGRATIONS:
      "https://help.higharc.com/api--integrations/hWdwNq8ybs7WQGT3MbyHx1/51-overview/n8LwbgNkBpn6C5dt8VoUgC",
    CONFIG: {
      COMMUNITY_MAPS:
        "https://help.higharc.com/learn-config/nz2uhNBg32cufBUtgzXqNN/41-creating-community-maps/kMZhzzvuip8dvHqgnmGoKC",
    },
  },
} as const;

/**
 * Local storage, session data, and cookies
 */

export const LOCAL_STORAGE_KEYS = {
  SHOW_HOME_BANNER: getPrefixedKey("show-home-banner"),
  STUDIO_HIDE_UNTITLED_HOMES: getPrefixedKey("studio-hide-untitled-homes"),
  STUDIO_HIDE_DETACHED_HOMES: getPrefixedKey("studio-hide-detached-homes"),
  DATA_GRID_VIEW_MODE: getPrefixedKey("data-grid-view-mode"),
  GLTF_EXPORT_GROUPS: getPrefixedKey("gltf-export-groups"),
  GLTF_MATERIAL_GEOMETRY_GROUPS: getPrefixedKey("gltf-material-geometry-groups"),
  GLTF_EXPORT_MODE: getPrefixedKey("gltf-export-mode"),
  RELEASE_NOTES_COUNT: getPrefixedKey("release-notes-count"),
} as const;

/**
 * React components
 */

export const PAGE_WRAPPER_ID = "PageWrapper";
export const DEBUG_EXPORT_PAGE_WRAPPER_ID = "Debug_PageWrapper";

// TODO: Duplicated in packages/ui/styles/zindex, should unify via code generation
export const Z_INDEX_VALUES = {
  TOOLTIP: 10001,
  MODAL_SCREEN: 10000,
  MODAL: 9999,
};

/**
 * Request helpers
 */

/** Base path for all browser/user API requests. External client apps use /api/v1. */
export const WEB_API_BASE_PATH = "/api/web/v1";

export const CORS_PROXY_REGEX = /^(https?:\/\/)([\d\.a-z-]+)(.*)(amazonaws.com)(.*)$/i;

export const DEFAULT_ASSET_REQUEST_TIMEOUT = 30000; // 30 seconds
export const DEFAULT_REQUEST_TIMEOUT = 10000; // 10 seconds
export const LAMDA_REQUEST_TIMEOUT = 45000; // 45 seconds
export const MAX_REQUEST_TIMEOUT = 120000; // 2 minutes
export const REDIRECT_DELAY_MS = 1250;

/**
 * Web forms, display values, and keyboard interactions
 */

export const FALLBACK_VALUE = "--";

export const ALPHANUMERIC_REGEX = /[a-z0-9]/i;
export const EMAIL_REGEX = /^\S+@\S+\.\S+$/;
export const WEBSITE_URL_REGEX =
  /^(https?:\/\/)?(www.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)?/;

/**
 * keyboard event .code values:
 */
export const CODE_TAB = "Tab";
export const CODE_ENTER = "Enter";
export const CODE_SHIFTLEFT = "ShiftLeft";
export const CODE_SHIFTRIGHT = "ShiftRight";
export const CODE_CTRLLEFT = "ControlLeft";
export const CODE_CTRLRIGHT = "ControlRight";
export const CODE_ALTLEFT = "AltLeft";
export const CODE_ALTRIGHT = "AltRight";
export const CODE_METALEFT = "MetaLeft";
export const CODE_METARIGHT = "MetaRight";
export const CODE_ESCAPE = "Escape";
export const CODE_ARROWUP = "ArrowUp";
export const CODE_ARROWDOWN = "ArrowDown";
export const CODE_ARROWLEFT = "ArrowLeft";
export const CODE_ARROWRIGHT = "ArrowRight";
export const CODE_HOME = "Home";
export const CODE_END = "End";
export const CODE_PRINTSCREEN = "PrintScreen";
export const CODE_CAPSLOCK = "CapsLock";
export const CODE_BACKSPACE = "Backspace";
export const CODE_DELETE = "Delete";

/**
 * Server and file upload variables
 */

export const FILE_SIZES = {
  B: 1,
  KB: 1024,
  MB: 1024 * 1024,
};

// Codes our application is equipped to respond with and handle on the client
export const STATUS_CODES = {
  OK: 200,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  GONE: 410,
  REQUEST_ENTITY_TOO_LARGE: 413,
  LOCKED: 423,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
} as const;

/**
 * Notifications
 */

export const NOTIFICATION_TIMEOUTS = {
  DEFAULT: 5000,
  REDIRECT: 2000,
} as const;

/** Thumbnail URLs */
export const PROP_THUMBNAILS_PATH = "thumbnails/props";

/**
 * Lambda
 */

// The path on S3 (relative to bucket root) where the generated plan analysis is stored.
export const PLAN_ANALYSIS_S3_PATH = "analysis/analysis.json";
